import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getTransportEmoji, getModeLabel, getEmissionLevel, EMISSION_LEVEL_COLOR, EMISSION_LEVEL_LABEL, isNightTrainMode } from '../../../shared/transport';
import Tooltip from '../../../components/ui/Tooltip';
import { NIGHT_TRAIN_TOOLTIP, bedsChip, fmtRide, modeTableNote } from '../nightTrain';
import { formatDurationMin, formatDurationExact, formatEuroExact, parseDurationInput, parseEuroInput, overridesKey } from '../utils';
import { CompareModeRow, LegUserOverrides } from '../../journeys/api';
import { Dots } from './RouteTimeline';

// Compare-transport-options dialog (prototype cmpx — see
// docs/prototypes/journey-detail/jody-transport-compare-extension.md).
// A centered modal ABOVE the leg sheet: one row per mode with time, cost and
// CO2e side by side; radio semantics; sortable Time/Cost/CO2e headers; n/a rows
// greyed and always last. Time and Cost cells are USER-EDITABLE per mode
// (userOverrides): tap a value to type your own; commit an EMPTY box to go
// back to the estimate. Overridden values render solid with a pencil glyph and
// are never clobbered by re-pricing (estimates live apart). Selection + edits
// mutate a DIALOG-LOCAL draft; "Save changes" hands both back to the LEG
// SHEET's draft (the sheet's Save stays the single commit point).
//
// Night trains (spec docs/product/night-trains/spec/night-trains-spec.md
// §7.3): the night-train row is always in the table. Available: next to
// Train, with the "Beds on board" or "Seats only" chip and the timetable's
// ride as its time. Not available: greyed, no explanation. Unknown: greyed,
// with one line, "No timetable data for Spain yet." The info icon explains
// night trains in every state.

export interface CompareBand { lowCents: number; highCents: number }

export interface CompareTransportDialogProps {
  open: boolean;
  from: string;
  to: string;
  distanceKm: number | null;
  /** Facts rows (null while still loading). */
  rows: CompareModeRow[] | null;
  error?: boolean;
  /** Per-mode price bands from the batched LLM call; missing + !pricesLoading = unavailable. */
  prices: Record<string, CompareBand | undefined>;
  pricesLoading: boolean;
  /** The leg-sheet draft's current mode when the dialog opened. */
  initialMode: string;
  /** The leg-sheet draft's overrides when the dialog opened (dialog snapshots them). */
  initialOverrides: LegUserOverrides;
  onSave: (mode: string, overrides: LegUserOverrides) => void;
  onClose: () => void;
}

type SortKey = 'time' | 'cost' | 'kg';
type CellField = 'costCents' | 'durationMinutes';

const bandStr = (b: CompareBand): string => {
  const lo = Math.round(b.lowCents / 100);
  const hi = Math.round(b.highCents / 100);
  return lo === hi ? `€${lo}` : `€${lo}–${hi}`;
};


const headBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 3, background: 'none', border: 'none', padding: 0,
  font: 'inherit', fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer',
};

const PencilGlyph: React.FC = () => (
  <svg width={9.5} height={9.5} viewBox="0 0 24 24" fill="none" aria-hidden style={{ flex: 'none' }}>
    <path d="M14.5 5.5l4 4M3 21l4-1L20 7a2 2 0 0 0-3-3L4 17l-1 4z" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CompareTransportDialog: React.FC<CompareTransportDialogProps> = ({ open, from, to, distanceKm, rows, error, prices, pricesLoading, initialMode, initialOverrides, onSave, onClose }) => {
  const [selected, setSelected] = useState(initialMode);
  const [sort, setSort] = useState<{ key: SortKey | null; asc: boolean }>({ key: null, asc: true });
  // Dialog-local overrides draft (two-level draft: applied to the sheet draft
  // only on Save changes).
  const [ov, setOv] = useState<LegUserOverrides>({});
  const [editing, setEditing] = useState<null | { mode: string; field: CellField }>(null);
  const [editVal, setEditVal] = useState('');
  const [editInvalid, setEditInvalid] = useState(false);
  const editingRef = useRef(false);
  editingRef.current = editing != null;

  useEffect(() => {
    if (open) {
      setSelected(initialMode);
      setOv(initialOverrides || {});
      setSort({ key: null, asc: true });
      setEditing(null);
      setEditInvalid(false);
    }
    // Snapshot the sheet draft once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Dialog-first Escape: capture phase + stopPropagation so the sheet's own
  // Escape listener (bubble phase on document) never fires while we're open.
  // While a cell is being edited, Escape cancels the edit instead.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (editingRef.current) { setEditing(null); setEditInvalid(false); } else { onClose(); }
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const setCell = (mode: string, field: CellField, value: number | null) => {
    setOv((o) => {
      const entry = { ...(o[mode] || {}) };
      if (value == null) delete entry[field]; else entry[field] = value;
      const next = { ...o };
      if (entry.costCents == null && entry.durationMinutes == null) delete next[mode]; else next[mode] = entry;
      return next;
    });
  };

  const startEdit = (mode: string, field: CellField) => {
    setEditing({ mode, field });
    setEditVal('');
    setEditInvalid(false);
  };

  const commitEdit = () => {
    if (!editing) return;
    const raw = editVal.trim();
    if (!raw) { setCell(editing.mode, editing.field, null); setEditing(null); return; } // empty = back to estimate
    const parsed = editing.field === 'costCents' ? parseEuroInput(raw) : parseDurationInput(raw);
    if (parsed == null || (editing.field === 'durationMinutes' && parsed <= 0)) { setEditInvalid(true); return; }
    setCell(editing.mode, editing.field, parsed);
    setEditing(null);
  };

  const dirty = selected !== initialMode || overridesKey(ov) !== overridesKey(initialOverrides);

  const priceVal = (mode: string, free: boolean): number => {
    const c = ov[mode]?.costCents;
    if (c != null) return c;
    if (free) return 0;
    const b = prices[mode];
    if (!b) return Number.POSITIVE_INFINITY; // unknown sorts last within available rows
    return (b.lowCents + b.highCents) / 2;
  };

  const ordered = useMemo(() => {
    if (!rows) return [];
    const avail = rows.filter((r) => r.applicable);
    const na = rows.filter((r) => !r.applicable);
    if (sort.key) {
      const val = (r: CompareModeRow): number => {
        if (sort.key === 'time') return ov[r.mode]?.durationMinutes ?? r.durationMinutes ?? Number.POSITIVE_INFINITY;
        if (sort.key === 'kg') return r.co2eKg ?? Number.POSITIVE_INFINITY;
        return priceVal(r.mode, r.free);
      };
      avail.sort((a, b) => (sort.asc ? val(a) - val(b) : val(b) - val(a)));
    }
    return [...avail, ...na]; // n/a rows always last (spec §4)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort, prices, ov]);

  if (!open) return null;

  const kmLabel = distanceKm != null && Number.isFinite(Number(distanceKm))
    ? `${Math.round(Number(distanceKm) / 10) * 10} km`
    : null;

  const sortHeader = (label: string, key: SortKey) => {
    const active = sort.key === key;
    return (
      <button
        type="button"
        style={{ ...headBtn, color: active ? '#0e5c40' : '#b3bbb6' }}
        aria-label={`Sort by ${label}`}
        onClick={() => setSort((s) => (s.key === key ? { key, asc: !s.asc } : { key, asc: true }))}
      >
        {label} <span aria-hidden>{active ? (sort.asc ? '▲' : '▼') : '↕'}</span>
      </button>
    );
  };

  // One editable value cell (time or cost). Estimate: dashed underline.
  // Overridden: solid + green pencil. Editing: small input (Enter/blur commits,
  // Escape cancels, EMPTY commit returns to the estimate).
  const editableCell = (mode: string, field: CellField, overridden: boolean, display: React.ReactNode, ariaLabel: string) => {
    const isThis = editing != null && editing.mode === mode && editing.field === field;
    if (isThis) {
      return (
        <input
          autoFocus
          value={editVal}
          placeholder={field === 'costCents' ? '€' : 'h:mm'}
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { setEditVal(e.target.value); setEditInvalid(false); }}
          onBlur={commitEdit}
          onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); }}
          style={{
            width: '100%', boxSizing: 'border-box', font: 'inherit', fontSize: 12, fontWeight: 700, color: '#16241d',
            padding: '3px 5px', borderRadius: 7, outline: 'none',
            border: `1.5px solid ${editInvalid ? '#cf5a3c' : '#157a55'}`,
          }}
        />
      );
    }
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); startEdit(mode, field); }}
        aria-label={`Edit ${ariaLabel}`}
        title={overridden ? 'Your value. Tap to change it.' : 'Estimated. Tap to enter your own.'}
        style={{
          background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 3, maxWidth: '100%',
          borderBottom: overridden ? 'none' : '1.5px dashed #d3d9d5', paddingBottom: overridden ? 0 : 1,
        }}
      >
        {overridden && <span style={{ color: '#157a55', display: 'inline-flex' }}><PencilGlyph /></span>}
        {display}
      </button>
    );
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Compare transport options"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2800, background: 'rgba(16,30,24,0.42)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      {/* The Dots keyframes normally come from RouteTimeline's style block;
          redeclared here so the loader animates even if this dialog is ever
          mounted without a RouteTimeline on the page. */}
      <style>{'@keyframes lp-blink{0%,80%,100%{opacity:.2}40%{opacity:1}}.lp-dot{animation:lp-blink 1.2s infinite}.lp-d2{animation-delay:.2s}.lp-d3{animation-delay:.4s}'}</style>
      <div style={{ width: '100%', maxWidth: 400, background: '#fff', borderRadius: 20, padding: '18px 16px 14px', boxShadow: '0 24px 60px rgba(20,40,30,0.25)' }}>
        <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 16.5, color: '#16241d' }}>Compare transport options</div>
        <div style={{ fontSize: 11.5, color: '#6c7872', margin: '3px 0 12px' }}>{from} → {to}{kmLabel ? ` · ${kmLabel}` : ''}</div>

        {error ? (
          <div style={{ fontSize: 13, color: '#6c7872', padding: '12px 0' }}>Could not load the comparison. Please close and try again.</div>
        ) : !rows ? (
          <div style={{ fontSize: 13, color: '#6c7872', padding: '12px 0' }}>Loading options…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '52vh', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '15px 1fr 58px 64px 58px', gap: 7, alignItems: 'center', padding: '2px 7px 6px' }}>
              <span />
              <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: '#b3bbb6' }}>Mode</span>
              {sortHeader('Time', 'time')}
              {sortHeader('Cost', 'cost')}
              {sortHeader('CO₂e', 'kg')}
            </div>
            {ordered.map((r) => {
              const sel = r.mode === selected && r.applicable;
              const level = getEmissionLevel(r.mode);
              const band = prices[r.mode];
              const timeOv = ov[r.mode]?.durationMinutes;
              const costOv = ov[r.mode]?.costCents;
              // A user time implies a refined distance, so the kg scale with it
              // (the Low/Medium/High level is mode-based and never changes).
              const kgVal = r.co2eKg == null ? null
                : timeOv != null && r.durationMinutes != null && Number(r.durationMinutes) > 0
                  ? Number(r.co2eKg) * (timeOv / Number(r.durationMinutes))
                  : Number(r.co2eKg);

              const night = isNightTrainMode(r.mode);
              const nightNote = night ? modeTableNote(r.nightTrainStatus, r.nightTrainNote) : null;
              // Greyed per cell, not on the row, so the unknown line under a
              // greyed night-train row stays readable.
              const dim = r.applicable ? 1 : 0.45;

              // A night train's time is the timetable's ride: exact, not a
              // rounded-up estimate.
              const estTime = r.durationMinutes == null ? '—'
                : (night && r.nightTrain ? fmtRide(Number(r.durationMinutes)) : formatDurationMin(r.durationMinutes));
              const timeDisplay = (
                <span style={{ fontSize: 12, fontWeight: 700, color: '#16241d', whiteSpace: 'nowrap' }}>
                  {timeOv != null ? formatDurationExact(timeOv) : estTime}
                </span>
              );
              const costDisplay = costOv != null
                ? <span style={{ fontSize: 12, fontWeight: 700, color: '#16241d', whiteSpace: 'nowrap' }}>{formatEuroExact(costOv)}</span>
                : band ? <span style={{ fontSize: 12, fontWeight: 700, color: '#16241d' }}>{bandStr(band)}</span>
                : pricesLoading ? <Dots />
                : <span style={{ fontSize: 12, color: '#b3bbb6' }}>n/a</span>;

              return (
                <div
                  key={r.mode}
                  role="radio"
                  aria-checked={sel}
                  aria-disabled={!r.applicable}
                  onClick={() => { if (r.applicable) setSelected(r.mode); }}
                  style={{
                    display: 'grid', gridTemplateColumns: '15px 1fr 58px 64px 58px', gap: 7, alignItems: 'center',
                    padding: '8px 7px', borderRadius: 11, cursor: r.applicable ? 'pointer' : 'default',
                    border: `1px solid ${sel ? '#cfe4d9' : 'transparent'}`,
                    background: sel ? '#e7f1ec' : 'transparent',
                  }}
                >
                  <span aria-hidden style={{ width: 15, height: 15, borderRadius: '50%', border: `1.7px solid ${sel ? '#157a55' : '#b3bbb6'}`, boxSizing: 'border-box', display: 'grid', placeItems: 'center', flex: 'none', opacity: dim }}>
                    {sel && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#157a55' }} />}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: '#16241d', minWidth: 0 }}>
                      <span aria-hidden style={{ fontSize: 15, flex: 'none', opacity: dim }}>{getTransportEmoji(r.mode)}</span>
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: dim }}>{getModeLabel(r.mode) || r.mode}</span>
                      {night && (
                        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', flex: 'none' }}>
                          <Tooltip content={NIGHT_TRAIN_TOOLTIP} widthClassName="w-64" bare>
                            <button type="button" aria-label="About night trains" style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#aab4af', fontWeight: 700, cursor: 'help' }}>ⓘ</button>
                          </Tooltip>
                        </span>
                      )}
                    </span>
                    {night && r.applicable && r.nightTrain && (
                      <span style={{ display: 'inline-block', marginTop: 3, fontSize: 10, fontWeight: 700, color: '#3b3c64', background: '#ecebf7', borderRadius: 7, padding: '2px 6px', whiteSpace: 'nowrap' }}>
                        {bedsChip(r.nightTrain)}
                      </span>
                    )}
                    {nightNote && <span style={{ display: 'block', marginTop: 2, fontSize: 10.5, fontWeight: 600, lineHeight: 1.3, color: '#6c7872' }}>{nightNote}</span>}
                  </span>
                  {r.applicable ? (
                    <>
                      <span>{editableCell(r.mode, 'durationMinutes', timeOv != null, timeDisplay, `${getModeLabel(r.mode)} travel time`)}</span>
                      <span>
                        {r.free && costOv == null
                          ? <span style={{ fontSize: 12, fontWeight: 700, color: '#6c7872' }}>Free</span>
                          : editableCell(r.mode, 'costCents', costOv != null, costDisplay, `${getModeLabel(r.mode)} price`)}
                      </span>
                      <span style={{ fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap', color: EMISSION_LEVEL_COLOR[level] }}>
                        {EMISSION_LEVEL_LABEL[level]}
                        <small style={{ display: 'block', fontSize: 10, fontWeight: 600, color: '#b3bbb6' }}>
                          {kgVal != null ? `${Math.round(kgVal)} kg` : ''}
                        </small>
                      </span>
                    </>
                  ) : (
                    <>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#b3bbb6', opacity: dim }}>n/a</span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#b3bbb6', opacity: dim }}>n/a</span>
                      <span style={{ fontSize: 11.5, fontWeight: 700, color: '#b3bbb6', opacity: dim }}>n/a</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div style={{ fontSize: 10.5, color: '#b3bbb6', marginTop: 9, lineHeight: 1.4 }}>
          Tap an option to select it for this leg. Prices are estimates*. Tap a time or price to enter your own (leave the box empty to go back to the estimate).
        </div>

        {dirty ? (
          <div style={{ display: 'flex', gap: 9, marginTop: 11 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, background: '#fff', border: '1px solid rgba(20,40,30,0.06)', borderRadius: 12, padding: 11, fontSize: 14, fontWeight: 700, color: '#6c7872', cursor: 'pointer' }}>Discard</button>
            <button type="button" onClick={() => onSave(selected, ov)} style={{ flex: 1.4, background: '#157a55', color: '#fff', border: 'none', borderRadius: 12, padding: 11, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Save changes</button>
          </div>
        ) : (
          <button type="button" onClick={onClose} style={{ width: '100%', marginTop: 11, background: '#157a55', color: '#fff', border: 'none', borderRadius: 12, padding: 11, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Done</button>
        )}
      </div>
    </div>,
    document.body
  );
};

export default CompareTransportDialog;
